import { matchesCurrentMoveOutRequest, moveOutStatusLabel, moveOutStatusClass } from '../../../utils/portvilleMoveOutRequest';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDocsFromServer, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../../hooks/useAuth';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { db } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';
import { MoveOutRequest } from '../../../types/moveOutRequest';

interface ResidencyInfo {
  residencyDocId?: string; // portville_residencies 문서 ID (신규 구조)
  roomNumber: string;
  contractorInfo: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  occupancyPeriod: {
    startDate: Date;
    endDate: Date;
  };
  signatureName: string;
  signatureDate: Date;
  contractSignedAt: Date;
  roomId: string;
  status: 'occupied' | 'contracted';
  landlordSignedContractUrl?: string;
  landlordSignedContractUploadedAt?: Date;
  hasExtended?: boolean; // 계약 연장 여부
  extensionAppliedAt?: Date; // 계약 연장 신청일
  extensionApprovedAt?: Date; // 계약 연장 승인일
  originalEndDate?: Date; // 원래 계약 종료일 (연장 전)
  // 연장계약 서명 정보
  extensionSignatureName?: string;
  extensionSignatureDate?: Date;
  extensionContractSignedAt?: Date;
  extensionLandlordSignedContractUrl?: string;
  extensionLandlordSignedContractUploadedAt?: Date;

  // 2차 연장계약(Second Extension) 정보
  hasSecondExtended?: boolean;
  secondExtensionAppliedAt?: Date;
  secondExtensionApprovedAt?: Date;
  secondOriginalEndDate?: Date;
  secondExtensionSignatureName?: string;
  secondExtensionSignatureDate?: Date;
  secondExtensionContractSignedAt?: Date;
  secondExtensionLandlordSignedContractUrl?: string;
  secondExtensionLandlordSignedContractUploadedAt?: Date;
}

interface ContractContent {
  article1: string;
  article2: string;
  article3: string;
  article4: string;
  article5: string;
  article6?: string;
  article7?: string;
  article8?: string;
  article9?: string;
  article10?: string;
  article11?: string;
  footer?: string;
  header?: string;
  signerInfo?: {
    name: string;
    address: string;
    residentNumber: string;
  };
  signatureUrl?: string;
}

const isResidencyPageDebugEnabled = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('debug_portville_residency') === '1';
  } catch {
    return false;
  }
};

const debugLog = (...args: any[]) => {
  if (isResidencyPageDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.log('[ResidencyPage]', ...args);
  }
};

const EXTENSION_APPLICATION_WINDOW_DAYS = 60;

const ResidencyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading: emailVerificationLoading } = useEmailVerification();
  
  const [loading, setLoading] = useState(true);
  const [residencyInfo, setResidencyInfo] = useState<ResidencyInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractContent, setContractContent] = useState<ContractContent | null>(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [currentContractType, setCurrentContractType] = useState<
    'original' | 'extension1' | 'extension2'
  >('original');
  const [moveOutRequest, setMoveOutRequest] = useState<MoveOutRequest | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading && !emailVerificationLoading) {
      loadResidencyInfo();
    }
  }, [user, authLoading, emailVerificationLoading]);

  const loadResidencyInfo = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      debugLog('loadResidencyInfo start', { uid: user.uid, at: new Date().toISOString() });

      const getMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value.toMillis === 'function') return value.toMillis();
          if (typeof value.toDate === 'function') return value.toDate().getTime();
          if (value instanceof Date) return value.getTime();
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? 0 : date.getTime();
        } catch {
          return 0;
        }
      };
      
      // 1. 새로운 데이터 구조에서 입주 기록 확인
      const residenciesRef = collection(db, 'portville_residencies');
      const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
      const residencySnapshot = await getDocs(residencyQuery);
      debugLog('residencies snapshot', { size: residencySnapshot.size, fromCache: residencySnapshot.metadata.fromCache });

      if (isResidencyPageDebugEnabled()) {
        try {
          const serverSnap = await getDocsFromServer(residencyQuery);
          debugLog('residencies server snapshot', { size: serverSnap.size, fromCache: serverSnap.metadata.fromCache });
        } catch (serverErr) {
          debugLog('residencies server snapshot failed', serverErr);
        }
      }
      
      if (!residencySnapshot.empty) {
        // 새로운 데이터 구조에서 입주 정보 로드
        // NOTE: ResidencyPage는 현재 진행 중인 입주/계약만 자동 진입 대상으로 본다.
        // completed/terminated 이력은 Portville 메인의 과거 이력 영역에서 다룬다.
        const residencyDocs = residencySnapshot.docs.map((snap) => ({
          id: snap.id,
          ...snap.data()
        })) as Array<Record<string, any> & { id: string }>;

        debugLog(
          'residency candidates',
          residencySnapshot.docs.map((snap) => {
            const data: any = snap.data();
            return {
              id: snap.id,
              fromCache: snap.metadata.fromCache,
              status: data?.status,
              roomId: data?.roomId,
              roomNumber: data?.roomNumber,
              updatedAtMillis: getMillis(data?.updatedAt),
              contractSignedAtMillis: getMillis(data?.contractSignedAt),
              hasContractContent: Boolean(data?.contractContent),
              hasExtensionContractContent: Boolean(data?.extensionContractContent),
              hasSecondExtensionContractContent: Boolean(data?.secondExtensionContractContent)
            };
          })
        );

	        const inProgressResidencies = residencyDocs
	          .filter((r) => r.status === 'active' || (r.status === 'contracting' && r.contractSignedAt))
          .sort((a, b) => {
            const aTime = getMillis(a.updatedAt) || getMillis(a.contractSignedAt) || getMillis(a.createdAt);
            const bTime = getMillis(b.updatedAt) || getMillis(b.contractSignedAt) || getMillis(b.createdAt);
            return bTime - aTime;
          });

        const residencyData = inProgressResidencies[0] ?? null;

        if (!residencyData) {
          debugLog('no in-progress residency; redirect /portville');
          navigate('/portville', { replace: true });
          return;
        }

        debugLog('selected residency', {
          id: residencyData?.id,
          status: residencyData?.status,
          roomId: residencyData?.roomId,
          roomNumber: residencyData?.roomNumber,
          updatedAtMillis: getMillis(residencyData?.updatedAt),
          contractSignedAtMillis: getMillis(residencyData?.contractSignedAt),
          hasContractContent: Boolean(residencyData?.contractContent)
        });
        debugLog('selected residency raw', residencyData);
        
        // 계약이 완료되지 않은 경우 포트빌 메인으로 리다이렉트
        if (!residencyData.contractSignedAt) {
          debugLog('contractSignedAt missing; redirect /portville', { residencyId: residencyData?.id });
          navigate('/portville', { replace: true });
          return;
        }
        
        setResidencyInfo({
          residencyDocId: residencyData.id,
          roomNumber: residencyData.roomNumber,
          contractorInfo: {
            name: residencyData.residentInfo?.name || '',
            phoneNumber: residencyData.residentInfo?.phoneNumber || '',
            organization: residencyData.residentInfo?.organization || ''
          },
          occupancyPeriod: {
            startDate: residencyData.occupancyPeriod?.startDate?.toDate() || new Date(),
            endDate: residencyData.occupancyPeriod?.endDate?.toDate() || new Date()
          },
          signatureName: residencyData.signatureName || '',
          signatureDate: residencyData.signatureDate?.toDate() || new Date(),
          contractSignedAt: residencyData.contractSignedAt?.toDate() || new Date(),
          roomId: residencyData.roomId,
          status: residencyData.status === 'active' ? 'occupied' : 'contracted',
          landlordSignedContractUrl: residencyData.landlordSignedContractUrl,
          landlordSignedContractUploadedAt: residencyData.landlordSignedContractUploadedAt?.toDate(),
          hasExtended: residencyData.hasExtended || false,
          extensionAppliedAt: residencyData.extensionAppliedAt?.toDate(),
          extensionApprovedAt: residencyData.extensionApprovedAt?.toDate(),
          originalEndDate: residencyData.originalEndDate?.toDate(),
          // 연장계약 서명 정보
          extensionSignatureName: residencyData.extensionSignatureName,
          extensionSignatureDate: residencyData.extensionSignatureDate?.toDate(),
          extensionContractSignedAt: residencyData.extensionContractSignedAt?.toDate(),
          extensionLandlordSignedContractUrl: residencyData.extensionLandlordSignedContractUrl,
          extensionLandlordSignedContractUploadedAt: residencyData.extensionLandlordSignedContractUploadedAt?.toDate(),

          // 2차 연장계약 서명 정보
          hasSecondExtended: residencyData.hasSecondExtended || false,
          secondExtensionAppliedAt: residencyData.secondExtensionAppliedAt?.toDate(),
          secondExtensionApprovedAt: residencyData.secondExtensionApprovedAt?.toDate(),
          secondOriginalEndDate: residencyData.secondOriginalEndDate?.toDate(),
          secondExtensionSignatureName: residencyData.secondExtensionSignatureName,
          secondExtensionSignatureDate: residencyData.secondExtensionSignatureDate?.toDate(),
          secondExtensionContractSignedAt: residencyData.secondExtensionContractSignedAt?.toDate(),
          secondExtensionLandlordSignedContractUrl: residencyData.secondExtensionLandlordSignedContractUrl,
          secondExtensionLandlordSignedContractUploadedAt:
            residencyData.secondExtensionLandlordSignedContractUploadedAt?.toDate()
        });
        
        return;
      }
      
      // 2. 기존 데이터 구조 호환성 확인
      const lotteryRef = collection(db, 'portville_lottery_history');
      const lotteryQuery = query(lotteryRef, where('uid', '==', user.uid));
      const lotterySnapshot = await getDocs(lotteryQuery);
      debugLog('lottery snapshot', { size: lotterySnapshot.size, fromCache: lotterySnapshot.metadata.fromCache });
      
      if (lotterySnapshot.empty) {
        // 추첨 기록이 없는 경우 - 이 페이지에 올 수 없는 상황
        debugLog('no lottery and no residencies; redirect /portville');
        navigate('/portville');
        return;
      }
      
      const lotteryData = lotterySnapshot.docs[0].data();
      const roomId = lotteryData.roomId;
      const status = lotteryData.status;
      
      if (status !== 'contracted') {
        // 계약이 완료되지 않은 경우 - 이 페이지에 올 수 없는 상황
        navigate('/portville');
        return;
      }
      
      // 3. 기존 구조에서 호실 정보 가져오기
      const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
      
      if (!roomDoc.exists()) {
        throw new Error('호실 정보를 찾을 수 없습니다.');
      }
      
      const roomData = roomDoc.data();
      debugLog('legacy roomData', { roomId, roomData });
      
                setResidencyInfo({
            roomNumber: roomData.roomNumber,
            contractorInfo: {
              name: roomData.currentResident?.name || '',
              phoneNumber: roomData.currentResident?.phoneNumber || '',
              organization: roomData.currentResident?.organization || ''
            },
            occupancyPeriod: {
              startDate: roomData.occupancyPeriod?.startDate?.toDate() || new Date(),
              endDate: roomData.occupancyPeriod?.endDate?.toDate() || new Date()
            },
            signatureName: roomData.signatureName || '',
            signatureDate: roomData.signatureDate?.toDate() || new Date(),
            contractSignedAt: roomData.contractSignedAt?.toDate() || new Date(),
            roomId,
            status: roomData.status === 'occupied' ? 'occupied' : 'contracted',
            landlordSignedContractUrl: roomData.landlordSignedContractUrl,
            landlordSignedContractUploadedAt: roomData.landlordSignedContractUploadedAt?.toDate(),
            hasExtended: roomData.hasExtended || false,
            extensionAppliedAt: roomData.extensionAppliedAt?.toDate(),
            extensionApprovedAt: roomData.extensionApprovedAt?.toDate(),
            originalEndDate: roomData.originalEndDate?.toDate(),
            // 연장계약 서명 정보
            extensionSignatureName: roomData.extensionSignatureName,
            extensionSignatureDate: roomData.extensionSignatureDate?.toDate(),
            extensionContractSignedAt: roomData.extensionContractSignedAt?.toDate(),
            extensionLandlordSignedContractUrl: roomData.extensionLandlordSignedContractUrl,
            extensionLandlordSignedContractUploadedAt: roomData.extensionLandlordSignedContractUploadedAt?.toDate(),

            hasSecondExtended: roomData.hasSecondExtended || false,
            secondExtensionAppliedAt: roomData.secondExtensionAppliedAt?.toDate(),
            secondExtensionApprovedAt: roomData.secondExtensionApprovedAt?.toDate(),
            secondOriginalEndDate: roomData.secondOriginalEndDate?.toDate(),
            secondExtensionSignatureName: roomData.secondExtensionSignatureName,
            secondExtensionSignatureDate: roomData.secondExtensionSignatureDate?.toDate(),
            secondExtensionContractSignedAt: roomData.secondExtensionContractSignedAt?.toDate(),
            secondExtensionLandlordSignedContractUrl: roomData.secondExtensionLandlordSignedContractUrl,
            secondExtensionLandlordSignedContractUploadedAt:
              roomData.secondExtensionLandlordSignedContractUploadedAt?.toDate()
          });
      
    } catch (error) {
      console.error('Error loading residency info:', error);
      setError('입주 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMoveOutRequest(null);
    if (residencyInfo) void checkMoveOutRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, residencyInfo?.residencyDocId, residencyInfo?.roomId]);

  const checkMoveOutRequest = async () => {
    if (!user || !residencyInfo) return;
    
    try {
      const currentResidency = residencyInfo.residencyDocId
        ? (await getDoc(doc(db, 'portville_residencies', residencyInfo.residencyDocId))).data()
        : undefined;
      const moveOutRequestsRef = collection(db, 'portville_move_out_requests');
      const moveOutRequestQuery = query(moveOutRequestsRef, where('residentUid', '==', user.uid));
      const moveOutRequestSnapshot = await getDocs(moveOutRequestQuery);
      
      const requestDoc = moveOutRequestSnapshot.docs
        .filter(snapshot => residencyInfo.residencyDocId
          ? matchesCurrentMoveOutRequest(snapshot.data() as any, {
              roomId: residencyInfo.roomId, residencyId: residencyInfo.residencyDocId,
              residentUid: user.uid, createdAt: currentResidency?.createdAt
            })
          : snapshot.data().roomId === residencyInfo.roomId && !snapshot.data().residencyId)
        .sort((a, b) => (b.data().appliedAt?.toMillis?.() || 0) - (a.data().appliedAt?.toMillis?.() || 0))[0];
      if (requestDoc) {
        const requestData = requestDoc.data();
        setMoveOutRequest({
          id: requestDoc.id,
          residencyId: requestData.residencyId,
          closureReason: requestData.closureReason,
          residentUid: requestData.residentUid,
          roomId: requestData.roomId,
          roomNumber: requestData.roomNumber,
          residentInfo: requestData.residentInfo,
          moveOutReason: requestData.moveOutReason,
          desiredMoveOutDate: requestData.desiredMoveOutDate?.toDate() || new Date(),
          attachments: requestData.attachments,
          status: requestData.status,
          appliedAt: requestData.appliedAt?.toDate() || new Date(),
          processedAt: requestData.processedAt?.toDate(),
          processedBy: requestData.processedBy,
          notes: requestData.notes
        });
      }
    } catch (error) {
      console.error('Error checking move out request:', error);
    }
  };

  const loadContractContent = async (
    contractType: 'original' | 'extension1' | 'extension2' = 'original'
  ) => {
    if (!user || !residencyInfo?.roomId) return;
    
    setCurrentContractType(contractType);
    
    try {
      setContractLoading(true);
      debugLog('loadContractContent start', {
        contractType,
        uid: user.uid,
        residencyDocId: residencyInfo.residencyDocId,
        roomId: residencyInfo.roomId,
        at: new Date().toISOString()
      });
      
      // 1. 먼저 새로운 데이터 구조에서 입주 기록 확인 (보안 검증)
      // NOTE: 쿼리 결과 순서는 보장되지 않으므로, 현재 화면에 로드된 입주 이력(문서 ID/roomId)과 매칭되는 문서를 사용한다.
      const residencyDocId = residencyInfo.residencyDocId;
      let residencyData: any | null = null;

      if (residencyDocId) {
        const residencyDoc = await getDoc(doc(db, 'portville_residencies', residencyDocId));
        if (residencyDoc.exists()) {
          residencyData = residencyDoc.data();
          debugLog('loadContractContent residencyDoc loaded', {
            id: residencyDoc.id,
            fromCache: residencyDoc.metadata.fromCache,
            status: residencyData?.status,
            roomId: residencyData?.roomId,
            hasContractContent: Boolean(residencyData?.contractContent),
            hasExtensionContractContent: Boolean(residencyData?.extensionContractContent),
            hasSecondExtensionContractContent: Boolean(residencyData?.secondExtensionContractContent)
          });
        } else {
          debugLog('loadContractContent residencyDocId not found', { residencyDocId });
        }
      }

      if (!residencyData) {
        const residenciesRef = collection(db, 'portville_residencies');
        const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
        const residencySnapshot = await getDocs(residencyQuery);
        debugLog('loadContractContent residencies snapshot', {
          size: residencySnapshot.size,
          fromCache: residencySnapshot.metadata.fromCache
        });

        if (!residencySnapshot.empty) {
          const roomIdKey = String(residencyInfo.roomId);
          const matchedByRoom = residencySnapshot.docs.find((snap) => String(snap.data().roomId) === roomIdKey);
          const selected = matchedByRoom ?? residencySnapshot.docs[0];
          residencyData = selected.data();
          debugLog('loadContractContent residency selected from query', {
            id: selected.id,
            fromCache: selected.metadata.fromCache,
            matchedByRoom: Boolean(matchedByRoom),
            status: residencyData?.status,
            roomId: residencyData?.roomId,
            hasContractContent: Boolean(residencyData?.contractContent),
            hasExtensionContractContent: Boolean(residencyData?.extensionContractContent),
            hasSecondExtensionContractContent: Boolean(residencyData?.secondExtensionContractContent)
          });
        }
      }

      if (residencyData) {
        const hasPermission =
          residencyData.residentUid === user.uid &&
          String(residencyData.roomId) === String(residencyInfo.roomId);

        if (!hasPermission) {
          debugLog('loadContractContent permission denied', {
            residencyResidentUid: residencyData.residentUid,
            userUid: user.uid,
            residencyRoomId: residencyData.roomId,
            expectedRoomId: residencyInfo.roomId
          });
          throw new Error('계약서 열람 권한이 없습니다.');
        }
      } else {
        // 기존 데이터 구조 호환성 확인
        const roomDoc = await getDoc(doc(db, 'portville_rooms', residencyInfo.roomId));
        if (!roomDoc.exists() || roomDoc.data().contractorUid !== user.uid) {
          debugLog('loadContractContent legacy permission denied', {
            roomId: residencyInfo.roomId,
            roomDocExists: roomDoc.exists()
          });
          throw new Error('계약서 열람 권한이 없습니다.');
        }
      }

      // 2. 새로운 데이터 구조에서 입주 기록에 계약 내용이 있는지 먼저 확인
      if (residencyData) {
        debugLog('residencyData used for contract content', {
          contractType,
          hasContractContent: Boolean(residencyData.contractContent),
          hasExtensionContractContent: Boolean(residencyData.extensionContractContent),
          hasSecondExtensionContractContent: Boolean(residencyData.secondExtensionContractContent)
        });
        
        // 연장계약서인지 일반계약서인지에 따라 다른 내용 로딩
        if (contractType === 'extension1' || contractType === 'extension2') {
          const isSecondExtension = contractType === 'extension2';
          const storedExtensionContent = isSecondExtension
            ? residencyData.secondExtensionContractContent
            : residencyData.extensionContractContent;
          const extensionSignatureUrl = isSecondExtension
            ? residencyData.secondExtensionSignatureUrl
            : residencyData.extensionSignatureUrl;
          const previousEndDate = isSecondExtension ? residencyData.secondOriginalEndDate : residencyData.originalEndDate;

          // 연장계약서 내용 로딩
          const hasStoredExtensionContract =
            storedExtensionContent && (storedExtensionContent.article1 || storedExtensionContent.article2);
          
          if (hasStoredExtensionContract) {
            debugLog('using stored extension contract content', { contractType });
            setContractContent({
              article1: storedExtensionContent?.article1 || '',
              article2: storedExtensionContent?.article2 || '',
              article3: storedExtensionContent?.article3 || '',
              article4: storedExtensionContent?.article4 || '',
              article5: storedExtensionContent?.article5 || '',
              article6: storedExtensionContent?.article6 || '',
              article7: storedExtensionContent?.article7 || '',
              article8: storedExtensionContent?.article8 || '',
              article9: storedExtensionContent?.article9 || '',
              article10: storedExtensionContent?.article10 || '',
              article11: storedExtensionContent?.article11 || '',
              footer: storedExtensionContent?.footer || '',
              header: storedExtensionContent?.header || '',
              signerInfo: storedExtensionContent?.signerInfo || {
                name: '',
                address: '',
                residentNumber: ''
              },
              signatureUrl: extensionSignatureUrl || ''
            });
          } else {
            debugLog('generating extension contract content from residency fields', { contractType });
            const originalEndDate = previousEndDate?.toDate?.()?.toLocaleDateString('ko-KR') || '';
            const currentEndDate = residencyData.occupancyPeriod?.endDate?.toDate()?.toLocaleDateString('ko-KR') || '';
            const residentName = residencyData.residentInfo?.name || '';
            const residentPhone = residencyData.residentInfo?.phoneNumber || '';
            const residentOrg = residencyData.residentInfo?.organization || '';
            
            setContractContent({
              article1: `제1조 (계약 당사자)

임대인: 부산항만공사
    주소: 부산광역시 중구 대교로 122 부산항만공사

임차인: ${residentName}
    연락처: ${residentPhone}
    소속: ${residentOrg}`,
              article2: `제2조 (임대차 목적물)

임대차 목적물은 다음과 같다.
- 소재지: 부산광역시 중구 대교로 122 부산항만공사 포트빌
- 호실번호: ${residencyData.roomNumber}호`,
              article3: `제3조 (계약 연장 기간)

계약 연장 기간은 다음과 같다.
- 원래 계약 종료일: ${originalEndDate}
- 연장 후 계약 종료일: ${currentEndDate}
- 연장 기간: 2년`,
              article4: `제4조 (임차인의 의무)

1. 임차인은 선량한 관리자의 주의로 임대차 목적물을 사용하여야 한다.
2. 임차인은 임대인의 동의 없이 임대차 목적물을 타인에게 전대하거나 임차권을 양도할 수 없다.
3. 임차인은 시설물 이용규칙을 준수하여야 한다.`,
              article5: `제5조 (계약의 효력)

1. 본 연장계약서는 쌍방 서명날인 시부터 효력을 발한다.
2. 본 계약에 명시되지 않은 사항은 관련 법령 및 시설 이용규칙에 따른다.`,
              signatureUrl: extensionSignatureUrl || ''
            });
          }
        } else {
          // 일반계약서 내용 로딩 (기존 로직)
          const hasStoredContract = residencyData.contractContent && 
            (residencyData.contractContent.article1 || residencyData.contractContent.article2);
          
          if (hasStoredContract) {
            debugLog('using stored original contract content');
            setContractContent({
              article1: residencyData.contractContent?.article1 || '',
              article2: residencyData.contractContent?.article2 || '',
              article3: residencyData.contractContent?.article3 || '',
              article4: residencyData.contractContent?.article4 || '',
              article5: residencyData.contractContent?.article5 || '',
              article6: residencyData.contractContent?.article6 || '',
              article7: residencyData.contractContent?.article7 || '',
              article8: residencyData.contractContent?.article8 || '',
              article9: residencyData.contractContent?.article9 || '',
              article10: residencyData.contractContent?.article10 || '',
              article11: residencyData.contractContent?.article11 || '',
              footer: residencyData.contractContent?.footer || '',
              header: residencyData.contractContent?.header || '',
              signerInfo: residencyData.contractContent?.signerInfo || {
                name: '',
                address: '',
                residentNumber: ''
              },
              signatureUrl: residencyData.signatureUrl || ''
            });
          } else {
            debugLog('generating original contract content from residency fields');
            const startDate = residencyData.occupancyPeriod?.startDate?.toDate()?.toLocaleDateString('ko-KR') || '';
            const endDate = residencyData.occupancyPeriod?.endDate?.toDate()?.toLocaleDateString('ko-KR') || '';
            const residentName = residencyData.residentInfo?.name || '';
            const residentPhone = residencyData.residentInfo?.phoneNumber || '';
            const residentOrg = residencyData.residentInfo?.organization || '';
            
            setContractContent({
              article1: `제1조 (계약 당사자)

임대인: 부산항만공사
    주소: 부산광역시 중구 대교로 122 부산항만공사

임차인: ${residentName}
    연락처: ${residentPhone}
    소속: ${residentOrg}`,
              article2: `제2조 (임대차 목적물)

임대차 목적물은 다음과 같다.
- 소재지: 부산광역시 중구 대교로 122 부산항만공사 포트빌
- 호실번호: ${residencyData.roomNumber}호`,
              article3: `제3조 (계약 기간)

임대차 기간은 다음과 같다.
- 계약 시작일: ${startDate}
- 계약 종료일: ${endDate}
- 계약 기간: 2년`,
              article4: `제4조 (임차인의 의무)

1. 임차인은 선량한 관리자의 주의로 임대차 목적물을 사용하여야 한다.
2. 임차인은 임대인의 동의 없이 임대차 목적물을 타인에게 전대하거나 임차권을 양도할 수 없다.
3. 임차인은 시설물 이용규칙을 준수하여야 한다.`,
              article5: `제5조 (계약의 효력)

1. 본 계약서는 쌍방 서명날인 시부터 효력을 발한다.
2. 본 계약에 명시되지 않은 사항은 관련 법령 및 시설 이용규칙에 따른다.`,
              signatureUrl: residencyData.signatureUrl || ''
            });
          }
        }
        
        setShowContractModal(true);
        return;
      }
      
      // 3. 기존 데이터 구조 호환성을 위한 계약 히스토리 조회 (백업)
      debugLog('fallback: querying portville_contracts backup (legacy)');
      const contractsRef = collection(db, 'portville_contracts');
      
      try {
        // residentUid로 검색 시도
        let contractQuery = query(
          contractsRef, 
          where('roomId', '==', residencyInfo.roomId),
          where('residentUid', '==', user.uid)
        );
        let contractSnapshot = await getDocs(contractQuery);
        
        // contractorUid로도 시도
        if (contractSnapshot.empty) {
          contractQuery = query(
            contractsRef, 
            where('roomId', '==', residencyInfo.roomId),
            where('contractorUid', '==', user.uid)
          );
          contractSnapshot = await getDocs(contractQuery);
        }
        
        if (!contractSnapshot.empty) {
          const contractData = contractSnapshot.docs[0].data();
          debugLog('fallback hit: contract content found in portville_contracts');
          
          setContractContent({
            article1: contractData.contractContent?.article1 || '',
            article2: contractData.contractContent?.article2 || '',
            article3: contractData.contractContent?.article3 || '',
            article4: contractData.contractContent?.article4 || '',
            article5: contractData.contractContent?.article5 || '',
            article6: contractData.contractContent?.article6 || '',
            article7: contractData.contractContent?.article7 || '',
            article8: contractData.contractContent?.article8 || '',
            article9: contractData.contractContent?.article9 || '',
            article10: contractData.contractContent?.article10 || '',
            article11: contractData.contractContent?.article11 || '',
            footer: contractData.contractContent?.footer || '',
            header: contractData.contractContent?.header || '',
            signerInfo: contractData.contractContent?.signerInfo || {
              name: '',
              address: '',
              residentNumber: ''
            },
            signatureUrl: contractData.signatureUrl || ''
          });
          
          setShowContractModal(true);
          return;
        }
      } catch (contractError) {
        debugLog('fallback contract history query failed', contractError);
      }
      
      // 4. 모든 방법이 실패한 경우 기본 템플릿 제공
      debugLog('fallback: using default template (no stored content found)');
      setContractContent({
        article1: `제1조 (계약 당사자)\n임대인: 부산항만공사\n임차인: ${residencyInfo.contractorInfo.name}`,
        article2: `제2조 (임대차 목적물)\n호실번호: ${residencyInfo.roomNumber}`,
        article3: `제3조 (계약 기간)\n시작일: ${residencyInfo.occupancyPeriod.startDate.toLocaleDateString()}\n종료일: ${residencyInfo.occupancyPeriod.endDate.toLocaleDateString()}`,
        article4: '제4조 (임차인의 의무)\n임차인은 선량한 관리자의 주의로 임대차 목적물을 사용하여야 한다.',
        article5: '제5조 (계약의 효력)\n본 계약서는 쌍방 서명날인 시부터 효력을 발한다.',
        signatureUrl: ''
      });
      
      setShowContractModal(true);
      
    } catch (error) {
      console.error('Error loading contract content:', error);
      alert(error instanceof Error ? error.message : '계약서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setContractLoading(false);
    }
  };

  const closeContractModal = () => {
    setShowContractModal(false);
    setContractContent(null);
    setCurrentContractType('original');
  };

  const getDaysUntilExpiry = () => {
    if (!residencyInfo) return null;
    const today = new Date();
    const expiryDate = residencyInfo.occupancyPeriod.endDate;
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryStatus = () => {
    const daysUntilExpiry = getDaysUntilExpiry();
    if (daysUntilExpiry === null) return null;
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 7) return 'critical';
    if (daysUntilExpiry <= EXTENSION_APPLICATION_WINDOW_DAYS) return 'warning';
    return 'normal';
  };

  if (authLoading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="flex flex-col min-h-screen bg-white">
          <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
            <div className="w-full max-w-[750px] px-4 sm:px-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <p className="text-red-800 text-center">{error}</p>
              </div>
              <button
                onClick={() => navigate('/portville')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                포트빌 메인으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!residencyInfo) {
    return null;
  }

  const expiryStatus = getExpiryStatus();
  const daysUntilExpiry = getDaysUntilExpiry();
  const extensionCount = (residencyInfo.hasExtended ? 1 : 0) + (residencyInfo.hasSecondExtended ? 1 : 0);
  const nextExtensionRound = extensionCount === 0 ? 1 : extensionCount === 1 ? 2 : null;
  const canApplyExtension =
    typeof daysUntilExpiry === 'number' &&
    daysUntilExpiry <= EXTENSION_APPLICATION_WINDOW_DAYS &&
    daysUntilExpiry >= 0 &&
    nextExtensionRound !== null;

  return (
    <ClientLayout>
      <div className="flex flex-col min-h-screen bg-white">
        <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
          <div className="w-full max-w-[750px] px-4 sm:px-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
              포트빌 입주 현황
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-normal text-slate-700 font-['Pretendard'] leading-9 mb-8">
              현재 입주중인 호실의 정보를 확인해주세요
            </p>
            <div className="w-full h-px bg-gray-200 mb-8"></div>

            <div className="space-y-6">
              {/* 입주 상태 표시 */}
              <div className={`border rounded-lg p-6 ${
                residencyInfo.status === 'occupied' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-center mb-4">
                  <svg 
                    className={`w-12 h-12 ${
                      residencyInfo.status === 'occupied' ? 'text-green-500' : 'text-blue-500'
                    }`}
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <path 
                      d="M9 22V12H15V22" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className={`text-xl font-bold text-center mb-2 ${
                  residencyInfo.status === 'occupied' ? 'text-green-800' : 'text-blue-800'
                }`}>
                  {residencyInfo.status === 'occupied' ? '현재 입주중' : '계약 완료'}
                </h2>
                <p className={`text-center ${
                  residencyInfo.status === 'occupied' ? 'text-green-700' : 'text-blue-700'
                }`}>
                  포트빌 <span className="font-bold">{residencyInfo.roomNumber}호</span>
                </p>
              </div>

              {/* 계약 만료 알림 */}
              {expiryStatus && expiryStatus !== 'normal' && (
                <div className={`border rounded-lg p-4 ${
                  expiryStatus === 'expired' 
                    ? 'bg-red-50 border-red-200' 
                    : expiryStatus === 'critical'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg 
                        className={`w-5 h-5 mr-2 ${
                          expiryStatus === 'expired' || expiryStatus === 'critical'
                            ? 'text-red-500' 
                            : 'text-yellow-500'
                        }`}
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div>
                        <p className={`text-sm font-medium ${
                          expiryStatus === 'expired' || expiryStatus === 'critical'
                            ? 'text-red-800' 
                            : 'text-yellow-800'
                        }`}>
                          {expiryStatus === 'expired' 
                            ? `계약이 ${Math.abs(daysUntilExpiry!)}일 전에 만료되었습니다.`
                            : expiryStatus === 'critical'
                            ? `계약이 ${daysUntilExpiry}일 후 만료됩니다.`
                            : `계약이 ${daysUntilExpiry}일 후 만료됩니다.`
                          }
                        </p>
                        {/* 계약 연장 가능한 경우 추가 안내 메시지 */}
                        {canApplyExtension && (
                          <p className="text-xs text-yellow-600 mt-1">
                            계약 연장은 최대 2회에 한해 가능합니다 (이번은 {nextExtensionRound}차 연장 신청)
                          </p>
                        )}
                      </div>
                    </div>
                    {/* 계약 연장 버튼 */}
                    {canApplyExtension && (
                      <button
                        onClick={() => navigate(`/portville/contract-extension?round=${nextExtensionRound}`)}
                        className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors"
                      >
                        계약연장 신청
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* 입주자 정보 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">입주자 정보</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">호실번호</dt>
                    <dd className="text-base font-semibold text-gray-900">{residencyInfo.roomNumber}호</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">입주자</dt>
                    <dd className="text-base font-semibold text-gray-900">{residencyInfo.contractorInfo.name}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">연락처</dt>
                    <dd className="text-base font-semibold text-gray-900">{residencyInfo.contractorInfo.phoneNumber}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">소속</dt>
                    <dd className="text-base font-semibold text-gray-900">{residencyInfo.contractorInfo.organization}</dd>
                  </div>
                </div>
              </div>

              {/* 입주 기간 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">입주 기간</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">입주 시작일</dt>
                    <dd className="text-base font-semibold text-gray-900">
                      {residencyInfo.occupancyPeriod.startDate.toLocaleDateString('ko-KR')}
                    </dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">계약 종료일</dt>
                    <dd className="text-base font-semibold text-gray-900">
                      {residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}
                    </dd>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    📅 계약 기간: 2년 ({residencyInfo.occupancyPeriod.startDate.toLocaleDateString('ko-KR')} ~ {residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')})
                  </p>
                </div>

                {/* 퇴거 신청 상태 */}
                {moveOutRequest ? (
                  <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-800 mb-1">
                          퇴거 신청이 완료되었습니다
                        </p>
                        <div className="text-xs text-orange-600 space-y-1">
                          <p>퇴거 희망일: {moveOutRequest.desiredMoveOutDate.toLocaleDateString('ko-KR')}</p>
                          <p>신청일: {moveOutRequest.appliedAt.toLocaleDateString('ko-KR')}</p>
                          <p>상태: {moveOutStatusLabel(moveOutRequest.status)}</p>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${moveOutStatusClass(moveOutRequest.status)}`}>
                        {moveOutStatusLabel(moveOutRequest.status)}
                      </div>
                    </div>
                    {moveOutRequest.notes && (
                      <div className="mt-3 p-3 bg-white border border-orange-200 rounded">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">관리자 메모:</span> {moveOutRequest.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* 퇴거 신청 */
                  (() => {
                    const today = new Date();
                    const endDate = residencyInfo.occupancyPeriod.endDate;
                    const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    
                    // 계약 종료일 하루 전까지 퇴거 신청 가능
                    if (daysUntilEnd >= 1) {
                      return (
                        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-red-800 mb-1">
                                퇴거 신청을 하고 싶으신가요?
                              </p>
                              <p className="text-xs text-red-600">
                                계약 종료일 하루 전까지 신청 가능 ({daysUntilEnd}일 남음)
                              </p>
                            </div>
                            <button
                              onClick={() => navigate('/portville/move-out-request')}
                              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                            >
                              퇴거 신청
                            </button>
                          </div>
                        </div>
                      );
                    }
                    
                    // 계약 종료일 당일 또는 이후에는 퇴거 신청 불가 안내
                    if (daysUntilEnd < 1) {
                      return (
                        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                퇴거 신청 기간이 만료되었습니다
                              </p>
                              <p className="text-xs text-gray-600">
                                계약 종료일 하루 전까지만 퇴거 신청이 가능합니다
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })()
                )}
              </div>

              {/* 계약 날인 정보 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">계약 날인 정보</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">서명자</dt>
                    <dd className="text-base font-semibold text-gray-900">{residencyInfo.signatureName}</dd>
                  </div>
                  
	                  <div>
	                    <dt className="text-sm font-medium text-gray-500">날인 완료일</dt>
	                    <dd className="text-base font-semibold text-gray-900">
	                      {residencyInfo.signatureDate.toLocaleDateString('ko-KR')}
	                    </dd>
	                  </div>
                </div>
              </div>

              {/* 일반 계약서 보기 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-blue-900 mb-2">계약서 열람</h3>
                    <p className="text-sm text-blue-700">
                      체결된 계약서의 전체 내용을 확인하실 수 있습니다.
                    </p>
                  </div>
                  <button
                    onClick={() => loadContractContent('original')}
                    disabled={contractLoading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {contractLoading && currentContractType === 'original' ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        로딩중...
                      </div>
                    ) : (
                      '계약서 보기'
                    )}
                  </button>
                </div>
              </div>

              {/* 1차 연장 계약서 보기 (연장계약이 체결된 경우에만 표시) */}
              {residencyInfo.extensionContractSignedAt && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-purple-900 mb-2">1차 연장계약서 열람</h3>
                      <p className="text-sm text-purple-700 mb-1">
                        체결된 연장계약서의 전체 내용을 확인하실 수 있습니다.
                      </p>
                      {residencyInfo.extensionSignatureName && residencyInfo.extensionContractSignedAt && (
                        <p className="text-xs text-purple-600">
                          연장계약 체결일: {residencyInfo.extensionContractSignedAt.toLocaleDateString('ko-KR')} 
                          (서명자: {residencyInfo.extensionSignatureName})
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => loadContractContent('extension1')}
                      disabled={contractLoading}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {contractLoading && currentContractType === 'extension1' ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          로딩중...
                        </div>
                      ) : (
                        '연장계약서 보기'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* 2차 연장 계약서 보기 (2차 연장계약이 체결된 경우에만 표시) */}
              {residencyInfo.secondExtensionContractSignedAt && (
                <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-fuchsia-900 mb-2">2차 연장계약서 열람</h3>
                      <p className="text-sm text-fuchsia-700 mb-1">
                        체결된 2차 연장계약서의 전체 내용을 확인하실 수 있습니다.
                      </p>
                      {residencyInfo.secondExtensionSignatureName &&
                        residencyInfo.secondExtensionContractSignedAt && (
                          <p className="text-xs text-fuchsia-600">
                            2차 연장계약 체결일:{' '}
                            {residencyInfo.secondExtensionContractSignedAt.toLocaleDateString('ko-KR')} (서명자:{' '}
                            {residencyInfo.secondExtensionSignatureName})
                          </p>
                        )}
                    </div>
                    <button
                      onClick={() => loadContractContent('extension2')}
                      disabled={contractLoading}
                      className="px-6 py-3 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {contractLoading && currentContractType === 'extension2' ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          로딩중...
                        </div>
                      ) : (
                        '2차 연장계약서 보기'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8">
              <button
                onClick={() => navigate('/portville')}
                className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
              >
                포트빌 메인으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 계약서 보기 모달 */}
	      {showContractModal && contractContent && (
	        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
	          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
	            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
	              <h2 className="text-xl font-bold text-gray-900">
	                {currentContractType === 'extension2'
	                  ? '포트빌 입주 2차 연장계약서'
	                  : currentContractType === 'extension1'
	                    ? '포트빌 입주 연장계약서'
	                    : '포트빌 입주 계약서'}
	              </h2>
	              <button
	                onClick={closeContractModal}
	                className="text-gray-400 hover:text-gray-600 focus:outline-none"
	              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
	            <div className="p-6 space-y-6">
	              <div className="text-center mb-8">
	                <h1 className="text-2xl font-bold text-gray-900 mb-2">
	                  {currentContractType === 'extension2'
	                    ? '포트빌 입주 2차 연장계약서'
	                    : currentContractType === 'extension1'
	                      ? '포트빌 입주 연장계약서'
	                      : '포트빌 입주 계약서'}
	                </h1>
                {contractContent.header && (
                  <div className="mb-2">
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.header}</p>
                  </div>
                )}
	                {(currentContractType === 'extension1' || currentContractType === 'extension2') &&
	                  (currentContractType === 'extension2'
	                    ? residencyInfo?.secondOriginalEndDate
	                    : residencyInfo?.originalEndDate) && (
	                    <p className="text-sm text-purple-600 mt-1">
	                      원래 계약 종료일:{' '}
	                      {(currentContractType === 'extension2'
	                        ? residencyInfo!.secondOriginalEndDate
	                        : residencyInfo!.originalEndDate
	                      )!.toLocaleDateString('ko-KR')}{' '}
	                      → 연장 후 종료일: {residencyInfo.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}
	                    </p>
	                  )}
	              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제1조 (계약 당사자)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article1}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제2조 (임대차 목적물)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article2}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제3조 (계약 기간)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article3}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제4조 (임차인의 의무)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article4}</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">제5조 (계약의 효력)</h3>
                  <p className="text-gray-700 whitespace-pre-line">{contractContent.article5}</p>
                </div>

                {contractContent.article6 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">제6조</h3>
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.article6}</p>
                  </div>
                )}

                {contractContent.article7 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">제7조</h3>
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.article7}</p>
                  </div>
                )}

                {contractContent.article8 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">제8조</h3>
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.article8}</p>
                  </div>
                )}

                {contractContent.article9 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">제9조</h3>
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.article9}</p>
                  </div>
                )}

                {contractContent.article10 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">제10조</h3>
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.article10}</p>
                  </div>
                )}

                {contractContent.article11 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">제11조</h3>
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.article11}</p>
                  </div>
                )}

                {contractContent.footer && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-line">{contractContent.footer}</p>
                  </div>
                )}

                {contractContent.signatureUrl && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-4">
                      {currentContractType === 'extension1' || currentContractType === 'extension2'
                        ? '연장계약자 서명'
                        : '계약자 서명'}
                    </h3>
                    <div className="flex flex-col items-center space-y-4">
                      <img 
                        src={contractContent.signatureUrl} 
                        alt={
                          currentContractType === 'extension1' || currentContractType === 'extension2'
                            ? '연장계약자 서명'
                            : '계약자 서명'
                        }
                        className="max-w-xs border border-gray-300 rounded"
                      />
                      <div className="text-center">
                        <p className="text-sm text-gray-600">
                          서명자:{' '}
                          {currentContractType === 'extension2'
                            ? residencyInfo?.secondExtensionSignatureName
                            : currentContractType === 'extension1'
                              ? residencyInfo?.extensionSignatureName
                              : residencyInfo?.signatureName}
                        </p>
                        <p className="text-sm text-gray-600">
                          서명일:{' '}
                          {(currentContractType === 'extension2'
                            ? residencyInfo?.secondExtensionSignatureDate
                            : currentContractType === 'extension1'
                              ? residencyInfo?.extensionSignatureDate
                              : residencyInfo?.signatureDate
                          )?.toLocaleDateString('ko-KR')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 임대인 날인본 섹션 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-4">
                    {currentContractType === 'extension2'
                      ? '임대인 2차 연장계약 날인본'
                      : currentContractType === 'extension1'
                        ? '임대인 연장계약 날인본'
                        : '임대인 날인본'}
                  </h3>
                  {(() => {
                    const landlordUrl =
                      currentContractType === 'extension2'
                        ? residencyInfo?.secondExtensionLandlordSignedContractUrl
                        : currentContractType === 'extension1'
                          ? residencyInfo?.extensionLandlordSignedContractUrl
                          : residencyInfo?.landlordSignedContractUrl;
                    const landlordUploadedAt =
                      currentContractType === 'extension2'
                        ? residencyInfo?.secondExtensionLandlordSignedContractUploadedAt
                        : currentContractType === 'extension1'
                          ? residencyInfo?.extensionLandlordSignedContractUploadedAt
                          : residencyInfo?.landlordSignedContractUploadedAt;
                    
                    console.log('=== 모달 날인본 URL 확인 ===');
                    console.log('currentContractType:', currentContractType);
                    console.log('landlordUrl:', landlordUrl);
                    console.log('landlordUploadedAt:', landlordUploadedAt);
                    console.log('residencyInfo.extensionLandlordSignedContractUrl:', residencyInfo?.extensionLandlordSignedContractUrl);
                    console.log(
                      'residencyInfo.secondExtensionLandlordSignedContractUrl:',
                      residencyInfo?.secondExtensionLandlordSignedContractUrl
                    );
                    console.log('residencyInfo.landlordSignedContractUrl:', residencyInfo?.landlordSignedContractUrl);
                    console.log('===============================');
                    
                    if (landlordUrl) {
                      return (
                        <div className="space-y-4">
                          <div className="bg-green-50 border border-green-200 rounded-md p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <div>
                                  <p className="text-green-800 font-medium">
                                    {currentContractType === 'extension2'
                                      ? '임대인 2차 연장계약 날인이 완료되었습니다.'
                                      : currentContractType === 'extension1'
                                        ? '임대인 연장계약 날인이 완료되었습니다.'
                                        : '임대인 날인이 완료되었습니다.'}
                                  </p>
                                  <p className="text-green-600 text-sm">
                                    업로드일: {landlordUploadedAt?.toLocaleDateString('ko-KR')}
                                  </p>
                                </div>
                              </div>
                              <a
                                href={landlordUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-2"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {currentContractType === 'extension2'
                                  ? '2차 연장계약 날인본 다운로드'
                                  : currentContractType === 'extension1'
                                    ? '연장계약 날인본 다운로드'
                                    : '날인본 다운로드'}
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                          <div className="flex items-center gap-3">
                            <svg className="h-5 w-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <p className="text-orange-800 font-medium">
                                {currentContractType === 'extension2'
                                  ? '임대인 2차 연장계약 날인 진행 중'
                                  : currentContractType === 'extension1'
                                    ? '임대인 연장계약 날인 진행 중'
                                    : '임대인 날인 진행 중'}
                              </p>
                              <p className="text-orange-700 text-sm">
                                {currentContractType === 'extension2'
                                  ? '임대인(부산항만공사)의 2차 연장계약 날인이 완료되면 날인본을 다운로드할 수 있습니다.'
                                  : currentContractType === 'extension1'
                                    ? '임대인(부산항만공사)의 연장계약 날인이 완료되면 날인본을 다운로드할 수 있습니다.'
                                    : '임대인(부산항만공사)의 날인이 완료되면 날인본을 다운로드할 수 있습니다.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 text-center text-sm text-gray-500">
	                <p>
	                  {currentContractType === 'extension2'
	                    ? `본 2차 연장계약서는 ${residencyInfo?.secondExtensionContractSignedAt?.toLocaleDateString('ko-KR')}에 체결되었습니다.`
	                    : currentContractType === 'extension1'
	                      ? `본 연장계약서는 ${residencyInfo?.extensionContractSignedAt?.toLocaleDateString('ko-KR')}에 체결되었습니다.`
	                      : `본 계약서는 ${residencyInfo?.signatureDate.toLocaleDateString('ko-KR')}에 체결되었습니다.`}
	                </p>
	              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={closeContractModal}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </ClientLayout>
  );
};

export default ResidencyPage; 
